'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import Drawer from '@/components/organisms/drawer';
import Accordion from '@/components/molecules/accordion';
import Button from '@/components/atoms/button';
import Toggle from '@/components/atoms/toggle';
import { classnames } from '@/utils/classnames/classnames';
import { AllFiltersDrawerV2Props, FilterDrawerSection, StagedFilters } from './types';

// Seed the staged map from the applied section values. Multi-select sections
// contribute a fresh string[] copy; toggle sections contribute their boolean.
const seedStaged = (sections: FilterDrawerSection[]): StagedFilters =>
  Object.fromEntries(sections.map((s) => [s.id, s.kind === 'toggle' ? s.value : [...s.value]]));

// EWM-185 right-side All Filters drawer.
//
// Composes the existing Drawer organism (slide-in shell) + Accordion
// molecules for each section. Multi-select sections render checkbox
// lists; toggle sections (e.g. In Stock) render a Toggle atom. Apply
// + Clear Filters buttons sit at the bottom — Apply uses the primary
// brand color, Clear is the `primary-outline` variant per the v0 mock
// (border-primary + text-primary on white).
//
// EWM-270 — footer buttons now use Dan v0's `text-15 font-bold` (the
// Button atom's `size="m"` ships `text-14 font-medium`; we override
// at the call site rather than changing the atom's m-size which is
// used app-wide). Clear uses the new `primary-outline` variant which
// matches Dan's `border-primary` colour, replacing the previous
// neutral-gray `secondary` variant.
//
// EWM-374 — batch apply. The drawer owns a *staged* copy of every section's
// selection, seeded from the applied (URL) values each time it opens. Ticking
// a checkbox / flipping a toggle mutates only the staged copy — nothing is
// committed until "Apply Filters" fires `onApply(staged)`. Closing the drawer
// without applying discards the staged edits (they re-seed on next open).
// This also removes the per-tick SSR round-trip that made the drawer feel
// laggy (EWM-372).
const AllFiltersDrawerV2 = ({ isOpen, sections, onApply, onClear, onClose }: AllFiltersDrawerV2Props) => {
  const translate = useTranslations();
  const [staged, setStaged] = useState<StagedFilters>(() => seedStaged(sections));

  // Re-seed from the applied values on each closed→open transition, so a
  // re-opened drawer reflects what's actually applied (not abandoned edits).
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (isOpen && !wasOpen.current) setStaged(seedStaged(sections));
    wasOpen.current = isOpen;
  }, [isOpen, sections]);

  const stagedTerms = (id: string): string[] => {
    const v = staged[id];
    return Array.isArray(v) ? v : [];
  };
  const stagedToggle = (id: string): boolean => staged[id] === true;

  const renderSection = (section: FilterDrawerSection) => {
    if (section.kind === 'toggle') {
      return (
        <div key={section.id} className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
          <span className="text-primary text-16 font-semibold">{section.label}</span>
          <Toggle
            checked={stagedToggle(section.id)}
            onChange={(next) => setStaged((prev) => ({ ...prev, [section.id]: next }))}
          />
        </div>
      );
    }

    const selected = stagedTerms(section.id);

    return (
      <Accordion
        key={section.id}
        defaultIsExpanded={section.defaultExpanded ?? false}
        borderless
        className="border-b border-gray-200"
      >
        <Accordion.Button density="spacious" chevronTone="primary">
          <span className="text-primary text-16 font-semibold">{section.label}</span>
          {selected.length > 0 && <span className="text-13 text-primary ml-2 font-normal">({selected.length})</span>}
        </Accordion.Button>
        <Accordion.Panel>
          {/* EWM-422 — long lists (Brand) cap to ~6 rows and scroll the rest
              inside the panel; other sections render their full list. */}
          <ul
            className={classnames('flex flex-col gap-2 px-6 pb-4', {
              // ~6 rows visible, then scroll (EWM-422 asked for 5–7).
              'max-h-44 overflow-y-auto': section.scrollable,
            })}
          >
            {section.options.length === 0 ? (
              <li className="text-14 text-gray-500">{translate('product.no-options')}</li>
            ) : (
              section.options.map((option) => {
                const checked = selected.includes(option.value);
                const onChange = () => {
                  const next = checked ? selected.filter((v) => v !== option.value) : [...selected, option.value];
                  setStaged((prev) => ({ ...prev, [section.id]: next }));
                };
                return (
                  <li key={option.value}>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={onChange}
                        className="accent-primary size-4 rounded border-gray-300"
                      />
                      <span className="text-14 text-gray-700">{option.label}</span>
                    </label>
                  </li>
                );
              })
            )}
          </ul>
        </Accordion.Panel>
      </Accordion>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      headline={translate('product.all-filters-heading')}
      direction="right"
      onClose={onClose ?? (() => undefined)}
    >
      <div className="flex h-full flex-col">
        <div className="flex-1 overflow-y-auto">{sections.map(renderSection)}</div>

        <div className="flex items-center gap-3 border-t border-gray-200 px-6 py-4">
          {/* EWM-597 — keep the two footer buttons the same height and aligned.
              Two issues, both worst on the narrow mobile drawer:
              1. `size="m"` ships `px-9` (36px) horizontal padding. With both
                 buttons `flex-1` (~164px each at 430px wide) that leaves ~88px
                 for the label, and "Apply Filters" is just wide enough to wrap
                 to two lines while "Clear Filters" stays on one — making Apply
                 ~16px taller. `!px-4` + `whitespace-nowrap` keeps both labels on
                 one line (the `!` is required: `classnames` doesn't merge, so a
                 plain `px-4` loses to the later-defined `px-9` in the cascade).
              2. The `primary-outline` Clear button's 2px border would make it
                 4px taller than the borderless `primary` Apply; a transparent
                 2px border on Apply equalises the box heights. */}
          <Button
            variant="primary"
            size="m"
            onClick={() => onApply?.(staged)}
            className="text-15 flex-1 border-2 border-transparent !px-4 font-bold whitespace-nowrap"
          >
            {translate('product.apply-filters')}
          </Button>
          <Button
            variant="primary-outline"
            size="m"
            onClick={() => {
              // Empty every staged section (multi-select → [], toggle → false)
              // so the UI updates immediately, then let the caller clear the
              // applied URL facets.
              setStaged((prev) =>
                Object.fromEntries(Object.entries(prev).map(([id, v]) => [id, typeof v === 'boolean' ? false : []])),
              );
              onClear?.();
            }}
            className="text-15 flex-1 !px-4 font-bold whitespace-nowrap"
          >
            {translate('product.clear-filters')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
};

export default AllFiltersDrawerV2;
